import React, { useState, useRef, useEffect, useCallback } from 'react';
import './HSLColorPicker.css';

const HSLColorPicker = ({ value, onChange, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hsl, setHsl] = useState({ h: 0, s: 100, l: 50 });
  const [hexInput, setHexInput] = useState(value || '#007cbf');
  const [isDragging, setIsDragging] = useState(null); // 'hue', 'sl', or null
  
  const pickerRef = useRef(null);
  const hueSliderRef = useRef(null);
  const slAreaRef = useRef(null);

  // Convert hex to HSL
  const hexToHsl = useCallback((hex) => {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Handle 3-digit hex
    if (hex.length === 3) {
      hex = hex.split('').map(char => char + char).join('');
    }
    
    // Handle 8-digit hex (with alpha) - just use first 6 digits
    if (hex.length === 8) {
      hex = hex.substring(0, 6);
    }
    
    if (hex.length !== 6) return { h: 0, s: 100, l: 50 };

    const r = parseInt(hex.substr(0, 2), 16) / 255;
    const g = parseInt(hex.substr(2, 2), 16) / 255;
    const b = parseInt(hex.substr(4, 2), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l;

    l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
        default: h = 0;
      }
      h /= 6;
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  }, []);

  // Convert HSL to hex
  const hslToHex = useCallback((h, s, l) => {
    h = h / 360;
    s = s / 100;
    l = l / 100;

    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    let r, g, b;

    if (s === 0) {
      r = g = b = l; // achromatic
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }

    const toHex = (c) => {
      const hex = Math.round(c * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }, []);

  // Initialize HSL from hex value
  useEffect(() => {
    if (value) {
      setHsl(hexToHsl(value));
      setHexInput(value);
    }
  }, [value, hexToHsl]);

  // Handle mouse events for hue slider
  const handleHueMouseDown = useCallback((e) => {
    setIsDragging('hue');
    updateHueFromEvent(e);
  }, []);

  const updateHueFromEvent = useCallback((e) => {
    if (!hueSliderRef.current) return;
    
    const rect = hueSliderRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const newHue = Math.round((x / rect.width) * 360);
    
    const newHsl = { ...hsl, h: newHue };
    setHsl(newHsl);
    
    const hex = hslToHex(newHsl.h, newHsl.s, newHsl.l);
    setHexInput(hex);
    onChange(hex);
  }, [hsl, hslToHex, onChange]);

  // Handle mouse events for saturation/lightness area
  const handleSLMouseDown = useCallback((e) => {
    setIsDragging('sl');
    updateSLFromEvent(e);
  }, []);

  const updateSLFromEvent = useCallback((e) => {
    if (!slAreaRef.current) return;
    
    const rect = slAreaRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));
    
    const newS = Math.round((x / rect.width) * 100);
    const newL = Math.round(100 - (y / rect.height) * 100);
    
    const newHsl = { ...hsl, s: newS, l: newL };
    setHsl(newHsl);
    
    const hex = hslToHex(newHsl.h, newHsl.s, newHsl.l);
    setHexInput(hex);
    onChange(hex);
  }, [hsl, hslToHex, onChange]);

  // Global mouse events
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging === 'hue') {
        updateHueFromEvent(e);
      } else if (isDragging === 'sl') {
        updateSLFromEvent(e);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(null);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, updateHueFromEvent, updateSLFromEvent]);

  // Handle hex input changes
  const handleHexInputChange = (e) => {
    let inputValue = e.target.value;
    setHexInput(inputValue);

    // Clean up the input - remove # and alpha channel if present
    let cleanHex = inputValue.replace('#', '');
    
    // Handle 8-digit hex (with alpha) - just use first 6 digits
    if (cleanHex.length === 8) {
      cleanHex = cleanHex.substring(0, 6);
    }
    
    // Validate hex color
    if (/^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(cleanHex)) {
      const fullHex = `#${cleanHex}`;
      setHsl(hexToHsl(fullHex));
      onChange(fullHex);
    }
  };

  const handleHexInputBlur = () => {
    // Ensure we have a valid hex color on blur
    let cleanHex = hexInput.replace('#', '');
    
    // Handle 8-digit hex (with alpha) - just use first 6 digits
    if (cleanHex.length === 8) {
      cleanHex = cleanHex.substring(0, 6);
    }
    
    if (!/^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(cleanHex)) {
      const currentHex = hslToHex(hsl.h, hsl.s, hsl.l);
      setHexInput(currentHex);
    } else {
      // Normalize to 6 digits
      if (cleanHex.length === 3) {
        cleanHex = cleanHex.split('').map(char => char + char).join('');
      }
      const normalizedHex = `#${cleanHex.toUpperCase()}`;
      setHexInput(normalizedHex);
    }
  };

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Calculate current color for preview
  const currentColor = hslToHex(hsl.h, hsl.s, hsl.l);
  
  // Calculate positions for sliders
  const huePosition = (hsl.h / 360) * 100;
  const slSatPosition = (hsl.s / 100) * 100;
  const slLightPosition = 100 - (hsl.l / 100) * 100;

  return (
    <div className="hsl-color-picker">
      <div className="style-control">
        <label>{label}:</label>
        <div className="color-picker-container" ref={pickerRef}>
          <div 
            className="color-picker-trigger" 
            onClick={() => setIsOpen(!isOpen)}
            style={{ backgroundColor: currentColor }}
            title={`Current color: ${currentColor}`}
          />
          
          {isOpen && (
            <div className="hsl-picker-popup">
              {/* Saturation/Lightness Area */}
              <div 
                className="sl-area"
                ref={slAreaRef}
                style={{
                  background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, hsl(${hsl.h}, 100%, 50%))`
                }}
                onMouseDown={handleSLMouseDown}
              >
                <div 
                  className="sl-cursor"
                  style={{
                    left: `${slSatPosition}%`,
                    top: `${slLightPosition}%`
                  }}
                />
              </div>
              
              {/* Hue Slider */}
              <div className="hue-slider-container">
                <div 
                  className="hue-slider"
                  ref={hueSliderRef}
                  onMouseDown={handleHueMouseDown}
                >
                  <div 
                    className="hue-cursor"
                    style={{ left: `${huePosition}%` }}
                  />
                </div>
              </div>
              
              {/* Hex Input */}
              <div className="hex-input-section">
                <label>Hex:</label>
                <input
                  type="text"
                  value={hexInput}
                  onChange={handleHexInputChange}
                  onBlur={handleHexInputBlur}
                  className="hex-input"
                  placeholder="#000000"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HSLColorPicker;